/**
 * TournamentBracket — renders a bracket view for any tournament format.
 *
 * Supports:
 *   single_elimination  – columnar round-by-round bracket
 *   double_elimination  – winners bracket, losers bracket and grand final sections
 *   round_robin         – rounds grouped as match lists
 *   swiss_system        – rounds grouped as match lists
 *   group_knockout      – one round-robin list per group, then the knockout bracket
 *
 * Bracket placement comes from match_config (bracket_type, bracket_round,
 * group), set by the backend when it creates the match.
 *
 * Props:
 *   matches          {Array}   flat match list (from /matches?tournament_id=...)
 *   tournamentType   {string}  e.g. "single_elimination"
 *   registrations    {Array}   registration list (used for round-robin standings)
 *   onMatchClick     {func}    (match) => void — called when a match card is clicked
 */

import React from "react";
import { ParticipantAvatar, matchSides } from "../ui/Matchup";

// ── helpers ───────────────────────────────────────────────────────────────────

const getName = (m, side) => {
  if (side === 1)
    return m.participant1Name || m.participant_1_name || m.home_team_name || m.participant_1_username || "TBD";
  return m.participant2Name || m.participant_2_name || m.away_team_name || m.participant_2_username || "TBD";
};

const asNumber = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * A side's score from the shapes scoreboards store: { participant1: 2 },
 * { participant1Score }, { participant1: { total | setsWon } }, or per-game
 * { games: [[11, 5], …] } / { gameScores: [{ p1, p2 }, …] } (sets won).
 */
const getScore = (m, side) => {
  const sd = m.scoreData || m.score_data || {};
  const key = side === 1 ? "participant1" : "participant2";
  const direct =
    asNumber(sd[key]) ?? asNumber(sd[key]?.setsWon) ?? asNumber(sd[`${key}Score`]) ?? asNumber(sd[side === 1 ? "score1" : "score2"]);
  if (direct !== null) return direct;
  const games = Array.isArray(sd.gameScores) && sd.gameScores.length
    ? sd.gameScores.map((g) => [g?.p1 ?? 0, g?.p2 ?? 0])
    : Array.isArray(sd.games) && sd.games.length ? sd.games : null;
  if (games) {
    return games.filter((g) => (side === 1 ? g[0] > g[1] : g[1] > g[0])).length;
  }
  // A live template ({ total: 0, … }) only counts once the match is played
  if (m.status === "completed") {
    const total = asNumber(sd[key]?.total);
    if (total !== null) return total;
  }
  return (side === 1 ? m.home_score ?? m.score_team1 : m.away_score ?? m.score_team2) ?? null;
};

const getWinnerId = (m) => m.winnerId ?? m.winner_id ?? null;
const getP1Id = (m) => m.participant1Id ?? m.participant_1_id ?? null;
const getP2Id = (m) => m.participant2Id ?? m.participant_2_id ?? null;
const getRound = (m) => m.round_number ?? m.round ?? 1;
const getConfig = (m) => m.matchConfig || m.match_config || {};
const getBracketType = (m) => getConfig(m).bracket_type ?? m.bracket_type ?? null;
/** Round within the match's own bracket (losers round 3, knockout round 1, …). */
const getBracketRound = (m) => getConfig(m).bracket_round ?? getRound(m);
const getGroup = (m) => getConfig(m).group ?? null;
const isDraw = (m) => m.result_type === "draw" || m.resultType === "draw";
const isCompleted = (m) => m.status === "completed" || !!getWinnerId(m) || isDraw(m);

/** Group matches by round number (or by `roundOf`), sorted ascending */
const groupByRound = (matches, roundOf = getRound) => {
  const map = {};
  matches.forEach((m) => {
    const r = roundOf(m);
    if (!map[r]) map[r] = [];
    map[r].push(m);
  });
  return map;
};

const sortedRounds = (map) =>
  Object.keys(map)
    .map(Number)
    .sort((a, b) => a - b);

// ── MatchCard ─────────────────────────────────────────────────────────────────

const MatchCard = ({ match: m, onClick, style = {} }) => {
  const p1Name = getName(m, 1);
  const p2Name = getName(m, 2);
  const s1 = getScore(m, 1);
  const s2 = getScore(m, 2);
  const p1Id = getP1Id(m);
  const p2Id = getP2Id(m);
  const winnerId = getWinnerId(m);
  const draw = isDraw(m);
  const completed = isCompleted(m);
  const p1Won = completed && !draw && winnerId !== null && winnerId === p1Id;
  const p2Won = completed && !draw && winnerId !== null && winnerId === p2Id;

  const rowBase = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.4rem 0.7rem",
    gap: "0.4rem",
  };

  const nameStyle = (won, lost) => ({
    flex: 1,
    fontSize: "0.83rem",
    fontWeight: won ? 700 : 400,
    color: won ? "#22c55e" : "#e2e8f0",
    textDecoration: lost ? "line-through" : "none",
    opacity: lost ? 0.45 : 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

  const scoreStyle = (won) => ({
    fontSize: "0.9rem",
    fontWeight: 700,
    color: won ? "#22c55e" : "#94a3b8",
    minWidth: "20px",
    textAlign: "right",
  });

  return (
    <div
      onClick={() => onClick && onClick(m)}
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(99,102,241,0.25)",
        borderRadius: "0.45rem",
        overflow: "hidden",
        cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s",
        minWidth: "180px",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(99,102,241,0.6)";
        e.currentTarget.style.background = "rgba(99,102,241,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(99,102,241,0.25)";
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
      }}
    >
      {/* Participant 1 */}
      <div style={{ ...rowBase, background: p1Won ? "rgba(34,197,94,0.08)" : "transparent" }}>
        <span style={nameStyle(p1Won, completed && !draw && !p1Won && winnerId !== null)}>
          <ParticipantAvatar size="xs" name={p1Name} image={matchSides(m)[0].image} kind={matchSides(m)[0].kind} className="bracket-avatar" />
          {p1Won && "✓ "}{p1Name}
        </span>
        {s1 !== null && <span style={scoreStyle(p1Won)}>{s1}</span>}
      </div>

      {/* Divider */}
      <div style={{ height: "1px", background: "rgba(99,102,241,0.15)" }} />

      {/* Participant 2 */}
      <div style={{ ...rowBase, background: p2Won ? "rgba(34,197,94,0.08)" : "transparent" }}>
        <span style={nameStyle(p2Won, completed && !draw && !p2Won && winnerId !== null)}>
          <ParticipantAvatar size="xs" name={p2Name} image={matchSides(m)[1].image} kind={matchSides(m)[1].kind} className="bracket-avatar" />
          {p2Won && "✓ "}{p2Name}
        </span>
        {s2 !== null && <span style={scoreStyle(p2Won)}>{s2}</span>}
      </div>

      {/* Draw badge */}
      {draw && (
        <div style={{ textAlign: "center", fontSize: "0.72rem", color: "#f59e0b", padding: "0.15rem", background: "rgba(245,158,11,0.1)" }}>
          Draw
        </div>
      )}
    </div>
  );
};

// ── RoundColumn ───────────────────────────────────────────────────────────────

/**
 * Name of an elimination round, counted back from the final of a bracket of
 * `totalRounds` (TN-B13: round 1 used to be called "Final" when later rounds
 * did not exist yet).
 */
const roundName = (round, totalRounds) => {
  const fromFinal = totalRounds - round;
  if (fromFinal === 0) return "🏆 Final";
  if (fromFinal === 1) return "Semifinals";
  if (fromFinal === 2) return "Quarterfinals";
  return round === 1 ? "First round" : `Round ${round}`;
};

const RoundLabel = ({ round, totalRounds, text }) => {
  const label = text || roundName(round, totalRounds);
  return (
    <div style={{
      textAlign: "center",
      color: "#a78bfa",
      fontWeight: 600,
      fontSize: "0.8rem",
      padding: "0.2rem 0.6rem",
      background: "rgba(99,102,241,0.12)",
      borderRadius: "0.3rem",
      marginBottom: "0.75rem",
      whiteSpace: "nowrap",
    }}>
      {label}
    </div>
  );
};

// ── SingleEliminationBracket ─────────────────────────────────────────────────

const ByeCard = ({ name }) => (
  <div style={{
    background: "rgba(34,197,94,0.07)",
    border: "1px solid rgba(34,197,94,0.3)",
    borderRadius: "0.45rem",
    overflow: "hidden",
    minWidth: "180px",
    padding: "0.6rem 0.7rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  }}>
    <span style={{ fontSize: "1rem" }}>🚀</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: "0.83rem", fontWeight: 700, color: "#22c55e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </div>
      <div style={{ fontSize: "0.72rem", color: "rgba(34,197,94,0.7)", marginTop: "0.1rem" }}>Bye — advances to Round 2</div>
    </div>
  </div>
);

/** A slot of a later round whose match does not exist yet. */
const PendingCard = ({ slot }) => {
  const label = (e) => (e?.bye ? "Bye" : e?.tbd || !e ? "TBD" : e.name || "TBD");
  return (
    <div data-testid="pending-slot" style={{
      background: "rgba(255,255,255,0.02)",
      border: "1px dashed rgba(99,102,241,0.3)",
      borderRadius: "0.45rem",
      minWidth: "180px",
      color: "#64748b",
      fontSize: "0.83rem",
    }}>
      <div style={{ padding: "0.4rem 0.7rem" }}>{label(slot.entrants?.[0])}</div>
      <div style={{ height: "1px", background: "rgba(99,102,241,0.12)" }} />
      <div style={{ padding: "0.4rem 0.7rem" }}>{label(slot.entrants?.[1])}</div>
    </div>
  );
};

const SingleEliminationBracket = ({ matches, onMatchClick, byeAdvancers = [], roundOf = getRound, roundLabel, totalRounds: knownTotal, skeleton = null }) => {
  const groups = groupByRound(matches, roundOf);
  // Later rounds may not exist yet; label Final/Semifinals against the full bracket when known
  const totalRounds = knownTotal || sortedRounds(groups).length;
  // Every round of the bracket, so later rounds show as TBD before their matches exist
  const rounds = knownTotal && skeleton
    ? Array.from({ length: knownTotal }, (_, i) => i + 1)
    : sortedRounds(groups);
  // Slots of a round that have no match yet (not bye slots of round 1)
  const pendingIn = (round) =>
    (skeleton || []).filter((s) =>
      s.round === round && !s.matchId && round > 1 && !(s.entrants || []).some((e) => e?.bye) &&
      !(groups[round] || []).some((m) => (getConfig(m).bracket_position || null) === s.label)
    );

  if (totalRounds === 0) {
    return <p style={{ color: "#64748b", textAlign: "center" }}>No matches yet.</p>;
  }

  return (
    <div style={{ display: "flex", gap: "1.5rem", overflowX: "auto", padding: "0.5rem 0 1rem", alignItems: "flex-start" }}>
      {rounds.map((round) => (
        <div key={round} style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <RoundLabel round={round} totalRounds={totalRounds} text={roundLabel ? roundLabel(round) : null} />
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {(groups[round] || []).map((m) => (
              <MatchCard key={m.id} match={m} onClick={onMatchClick} />
            ))}
            {pendingIn(round).map((slot) => (
              <PendingCard key={slot.label} slot={slot} />
            ))}
            {/* Show bye teams in Round 1 column only */}
            {round === 1 && byeAdvancers.map((b) => (
              <ByeCard key={`bye-${b.id}`} name={b.name} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── DoubleEliminationBracket ─────────────────────────────────────────────────

const sectionLabel = (text, color) => (
  <h4 style={{ color, margin: "0 0 0.75rem", fontSize: "0.9rem", fontWeight: 700 }}>{text}</h4>
);

const sectionStyle = { borderTop: "1px solid rgba(99,102,241,0.25)", paddingTop: "1.5rem" };

const DoubleEliminationBracket = ({ matches, onMatchClick, byeAdvancers = [], totalWBRounds }) => {
  // Matches created before bracket_type was stored are winners-bracket round 1
  const winners = matches.filter((m) => (getBracketType(m) ?? "winners") === "winners");
  const losers = matches.filter((m) => getBracketType(m) === "losers");
  const grandFinal = matches
    .filter((m) => getBracketType(m) === "grand_final")
    .sort((a, b) => getBracketRound(a) - getBracketRound(b));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
      <div>
        {sectionLabel("🏆 Winners Bracket", "#22c55e")}
        <SingleEliminationBracket
          matches={winners}
          onMatchClick={onMatchClick}
          byeAdvancers={byeAdvancers}
          roundOf={getBracketRound}
          totalRounds={totalWBRounds}
        />
      </div>

      {losers.length > 0 && (
        <div style={sectionStyle}>
          {sectionLabel("🔄 Losers Bracket", "#f59e0b")}
          <SingleEliminationBracket
            matches={losers}
            onMatchClick={onMatchClick}
            roundOf={getBracketRound}
            roundLabel={(r) => `Losers Round ${r}`}
          />
        </div>
      )}

      {grandFinal.length > 0 && (
        <div style={sectionStyle}>
          {sectionLabel("⚡ Grand Final", "#a78bfa")}
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            {grandFinal.map((m) => (
              <div key={m.id}>
                <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.3rem" }}>
                  {getBracketRound(m) === 2 ? "Reset match" : "Final"}
                </div>
                <MatchCard match={m} onClick={onMatchClick} style={{ minWidth: "220px" }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── GroupKnockoutBracket ─────────────────────────────────────────────────────

const GroupKnockoutBracket = ({ matches, onMatchClick, knockoutRounds }) => {
  const groupMatches = matches.filter((m) => getBracketType(m) === "group" || (!getBracketType(m) && getGroup(m)));
  const knockout = matches.filter((m) => getBracketType(m) === "knockout");
  const groupNames = [...new Set(groupMatches.map((m) => getGroup(m) || "?"))].sort();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
      <div>
        {sectionLabel("🧩 Group Stage", "#38bdf8")}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {groupNames.map((g) => (
            <div key={g}>
              <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Group {g}</div>
              <RoundRobinBracket matches={groupMatches.filter((m) => (getGroup(m) || "?") === g)} onMatchClick={onMatchClick} />
            </div>
          ))}
        </div>
      </div>

      <div style={sectionStyle}>
        {sectionLabel("🏆 Knockout", "#22c55e")}
        {knockout.length > 0 ? (
          <SingleEliminationBracket
            matches={knockout}
            onMatchClick={onMatchClick}
            roundOf={getBracketRound}
            totalRounds={knockoutRounds}
          />
        ) : (
          <p style={{ color: "#64748b", fontSize: "0.85rem", margin: 0 }}>
            The knockout is drawn once every group match is complete.
          </p>
        )}
      </div>
    </div>
  );
};

// ── RoundRobinBracket ─────────────────────────────────────────────────────────

const RoundRobinBracket = ({ matches, onMatchClick }) => {
  const groups = groupByRound(matches);
  const rounds = sortedRounds(groups);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {rounds.map((round) => (
        <div key={round}>
          <div style={{
            color: "#a78bfa",
            fontWeight: 600,
            fontSize: "0.82rem",
            marginBottom: "0.5rem",
            padding: "0.2rem 0.5rem",
            background: "rgba(99,102,241,0.1)",
            borderRadius: "0.25rem",
            display: "inline-block",
          }}>
            Round {round}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginTop: "0.4rem" }}>
            {groups[round].map((m) => (
              <MatchCard key={m.id} match={m} onClick={onMatchClick} style={{ minWidth: "200px", flex: "1 1 200px", maxWidth: "280px" }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── TournamentBracket (public export) ─────────────────────────────────────────

const TournamentBracket = ({ matches = [], tournamentType, registrations = [], byeAdvancers = [], progress = null, onMatchClick }) => {
  if (!matches.length) {
    return (
      <div style={{ textAlign: "center", padding: "2.5rem 0", color: "#64748b" }}>
        <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🎯</div>
        <p>No matches generated yet.</p>
      </div>
    );
  }

  if (tournamentType === "group_knockout") {
    return <GroupKnockoutBracket matches={matches} onMatchClick={onMatchClick} knockoutRounds={progress?.knockoutRounds} />;
  }

  if (tournamentType === "round_robin" || tournamentType === "swiss_system") {
    return <RoundRobinBracket matches={matches} registrations={registrations} onMatchClick={onMatchClick} />;
  }

  // For elimination brackets: derive bye teams not covered by byeAdvancers prop
  // (byeAdvancers from fresh generation; or compute from registrations vs R1 participants on reload)
  let resolvedByes = byeAdvancers;
  if (resolvedByes.length === 0 && registrations.length > 0) {
    const r1ParticipantIds = new Set(
      matches
        .filter((m) => (m.round_number ?? m.round ?? 1) === 1)
        .flatMap((m) => [getP1Id(m), getP2Id(m)])
        .filter(Boolean)
    );
    resolvedByes = registrations
      .filter((r) => r.registration_status === "approved")
      .filter((r) => {
        const pid = r.team_id || r.user_id;
        return pid && !r1ParticipantIds.has(pid);
      })
      .map((r) => ({
        id: r.team_id || r.user_id,
        name: r.team_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.username || `#${r.team_id || r.user_id}`,
      }));
  }

  if (tournamentType === "double_elimination") {
    return (
      <DoubleEliminationBracket
        matches={matches}
        onMatchClick={onMatchClick}
        byeAdvancers={resolvedByes}
        totalWBRounds={progress?.totalWBRounds}
      />
    );
  }

  // Default: single_elimination (also fallback for unknown types). The full
  // bracket (round count and TBD slots) comes from /progress (TN-B13).
  return (
    <SingleEliminationBracket
      matches={matches}
      onMatchClick={onMatchClick}
      byeAdvancers={resolvedByes}
      totalRounds={progress?.totalRounds}
      skeleton={progress?.skeleton || null}
    />
  );
};

export default TournamentBracket;

// Named exports for unit testing
export { MatchCard, ByeCard, PendingCard, roundName, SingleEliminationBracket, DoubleEliminationBracket, GroupKnockoutBracket, RoundRobinBracket, groupByRound, sortedRounds, getName, getScore, getWinnerId, isDraw, isCompleted, getBracketType, getBracketRound, getGroup };
